import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  TextField,
  Stack,
  Chip,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import LockIcon from '@mui/icons-material/Lock';
import { styled } from '@mui/material/styles';
import { useAuth } from '../services/auth.service';
import { usePermanentTokenService } from '../services/permanentToken.service';

const PageContainer = styled(Box)(() => ({
  padding: '24px',
  backgroundColor: '#f5f7fb',
  minHeight: 'calc(100vh - 64px)',
}));

const SectionCard = styled(Card)(() => ({
  borderRadius: 16,
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
}));

const TokenTextField = styled(TextField)(() => ({
  '& .MuiInputBase-root': {
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    fontSize: 12,
  },
}));

const InfoLabel = styled(Typography)(() => ({
  fontSize: 12,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: '#6b7280',
}));

export default function Profile() {
  const { user } = useAuth();
  const { getPermanentToken, generatePermanentToken } = usePermanentTokenService();

  const [loading, setLoading] = useState(false);
  const [tokenData, setTokenData] = useState(null);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const loadToken = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getPermanentToken();
      setTokenData(data);
    } catch (err) {
      if (err?.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Error loading permanent token.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await generatePermanentToken();
      setTokenData(data);
      showSnackbar('Permanent token generated successfully.', 'success');
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Error generating permanent token.';
      setError(message);
      showSnackbar(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!tokenData?.permanentToken) return;
    try {
      await navigator.clipboard.writeText(tokenData.permanentToken);
      showSnackbar('Token copied to clipboard.', 'success');
    } catch {
      showSnackbar('Unable to copy token to clipboard.', 'error');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  useEffect(() => {
    loadToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasToken = !!tokenData?.permanentToken;

  return (
    <PageContainer>
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Profile &amp; API Access
        </Typography>
        <Typography variant="body2" sx={{ color: '#6b7280', maxWidth: 520 }}>
          View your account information and manage your permanent API token for
          scripts, integrations, and automations.
        </Typography>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          {/* Basic user info */}
          <SectionCard sx={{ flex: 1 }}>
            <CardHeader
              title="Account"
              subheader="Basic information about your user."
              sx={{
                '& .MuiCardHeader-title': { fontWeight: 600, fontSize: 16 },
                '& .MuiCardHeader-subheader': { fontSize: 13 },
              }}
            />
            <CardContent>
              <Stack spacing={1.5}>
                <Box>
                  <InfoLabel>Username</InfoLabel>
                  <Typography sx={{ fontSize: 14, mt: 0.5 }}>
                    {user?.username || '—'}
                  </Typography>
                </Box>
                <Box>
                  <InfoLabel>Roles</InfoLabel>
                  <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap">
                    {(user?.roles || []).map((role) => (
                      <Chip
                        key={role}
                        size="small"
                        label={role}
                        sx={{ mb: 0.5 }}
                      />
                    ))}
                    {(!user?.roles || user.roles.length === 0) && (
                      <Typography sx={{ fontSize: 13, color: '#9ca3af' }}>
                        No roles available.
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </SectionCard>

          {/* Permanent Token */}
          <SectionCard sx={{ flex: 1.4 }}>
            <CardHeader
              avatar={<LockIcon color="primary" />}
              title="Permanent API Token"
              subheader="Non-expiring JWT token for programmatic access. Treat it like a password."
              sx={{
                '& .MuiCardHeader-title': { fontWeight: 600, fontSize: 16 },
                '& .MuiCardHeader-subheader': { fontSize: 13 },
              }}
            />
            <CardContent>
              <Stack spacing={1.5}>
                {error && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    {error}
                  </Alert>
                )}

                {hasToken ? (
                  <>
                    <InfoLabel>Current Token</InfoLabel>
                    <TokenTextField
                      value={tokenData.permanentToken}
                      multiline
                      minRows={3}
                      maxRows={4}
                      fullWidth
                      variant="outlined"
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <Tooltip title="Copy token">
                            <IconButton onClick={handleCopy} edge="end">
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ),
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: '#6b7280', mt: 0.5 }}
                    >
                      Created at: {formatDate(tokenData.createdAt)}
                    </Typography>
                  </>
                ) : (
                  <Alert severity="info">
                    No permanent token has been generated for this user yet.
                    Click &quot;Generate token&quot; to create one.
                  </Alert>
                )}

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  mt={1}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleGenerate}
                    disabled={loading}
                    startIcon={<RefreshIcon />}
                  >
                    {hasToken ? 'Regenerate token' : 'Generate token'}
                  </Button>
                  <Button
                    variant="outlined"
                    disabled={loading}
                    onClick={loadToken}
                  >
                    Refresh status
                  </Button>
                </Stack>

                <Typography
                  variant="caption"
                  sx={{ color: '#9ca3af', mt: 1.5, lineHeight: 1.6 }}
                >
                  This token has no built-in expiration and will work with any
                  backend route that accepts{' '}
                  <Box
                    component="span"
                    sx={{
                      fontFamily:
                        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                      backgroundColor: '#f3f4f6',
                      borderRadius: '4px',
                      px: 0.5,
                    }}
                  >
                    x-access-token: &lt;permanentToken&gt;
                  </Box>
                  . Click &quot;Regenerate token&quot; to rotate and invalidate
                  the previous value.
                </Typography>
              </Stack>
            </CardContent>
          </SectionCard>
        </Stack>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Stack>
    </PageContainer>
  );
}

